import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Pencil, Trash2 } from "lucide-react";
import Chip from "../../../../Shared/ui/Chip";
import { useTranslation } from "react-i18next";

function AccountCategoryTable({ categories, handleEditCategory, handleDeleteCategory, searchValue }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const formatTypeCell = (isRevenue) => {
    return isRevenue ? (
      <Chip variant={"success"} size="small">
        {t('accountCategoryTable.revenue')}
      </Chip>
    ) : (
      <Chip variant={"danger"} size="small">
        {t('accountCategoryTable.expense')}
      </Chip>
    );
  };

  const highlightText = (text, searchValue) => {
    if (!searchValue || !text || searchValue.trim() === "") return text;
    const trimmedSearchValue = searchValue.trim();
    const regex = new RegExp(`(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) => {
      if (part.toLowerCase() === trimmedSearchValue.toLowerCase()) {
        return (
          <mark key={index} style={{ backgroundColor: "var(--color-primary)", color: "#F8F8F8", padding: "0", borderRadius: "2px" }}>
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead className={classes.table__header}>
          <tr>
            <th>{t('common.name')}</th>
            <th>{t('user.company')}</th>
            <th>{t('common.type')}</th>
            <th style={{ minWidth: "16px" }} />
          </tr>
        </thead>
        <tbody className={classes.table__body}>
          {Array.isArray(categories) && categories.length > 0 ? (
            categories.map((category, index) => (
              <tr className={classes.table__row} key={index}>
                <td>{highlightText(category.name, searchValue)}</td>
                <td>{category.companyName}</td>
                <td>{formatTypeCell(category.isRevenue)}</td>
                <td>
                  <div className={classes.buttonContainer}>
                    <button className={classes.iconButton} onClick={() => handleEditCategory(category.id)}>
                      <Pencil className={classes.iconButton__icon} />
                    </button>
                    <button className={classes.iconButton} onClick={() => handleDeleteCategory(category.id)}>
                      <Trash2 className={classes.iconButton__icon} style={{ color: "#D84949" }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: "center", padding: "32px" }}>
                {t('accountCategoryTable.noCategories')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default AccountCategoryTable;

const useStyles = makeStyles({
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",

      "&:first-child": {
        borderTopLeftRadius: "8px",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200,

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },
});
